<template>
  <div>
    <el-upload v-bind="$attrs"
               v-on="$listeners"
               :on-success="onSuccess"
               :file-list="fileList"
               :on-exceed="onExceed"
               :limit="limit"
               :before-upload="beforeUpload"
               :accept="accept">
      <i slot="default"
         class="el-icon-plus"></i>
      <div slot="file"
           slot-scope="{file}">
        <!-- 根据文件类型展示图片或文件图标 -->
        <div v-if="isImage(file.url)">
          <el-image :src="'/resource/' + file.url"
                    class="image-container">
            <!-- 图片加载失败时展示的图标 -->
            <div slot="error"
                 class="image-slot">
              <i class="el-icon-picture-outline"></i>
            </div>
          </el-image>
        </div>
        <div v-else
             class="file-icon">
          <i class="el-icon-document"></i> <!-- 通用文件图标 -->
        </div>
        <span class="el-upload-list__item-actions">
            <span v-if="isImage(file.url)"
                  class="el-upload-list__item-delete"
                  @click="handlePictureCardPreview(file)">
              <i class="el-icon-zoom-in"></i>
            </span>
            <span v-if="showDownload"
                  class="el-upload-list__item-delete"
                  @click="downloadFile(file)">
              <i class="el-icon-download"></i>
            </span>
            <span v-if="showRemove"
                  class="el-upload-list__item-delete"
                  @click="removeFile(file)">
              <i class="el-icon-delete"></i>
            </span>
          </span>
      </div>
    </el-upload>
    <div v-show="fileList.length === 0 && !showImportControl"
         style="height: 115px">
      暂无上传文件
    </div>

    <el-dialog :visible.sync="dialogVisible">
      <img width="100%"
           :src="dialogImageUrl"
           alt="">
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'file-upload',

  model: {
    prop: 'value',
    event: 'change'
  },

  data() {
    return {
      dialogImageUrl: '',
      dialogVisible: false
    }
  },

  props: {
    value: {type: String, default: "[]"}, // 文件列表作为字符串传递
    showDownload: {type: Boolean, default: true}, // 控制是否显示下载按钮
    showRemove: {type: Boolean, default: true},   // 控制是否显示删除按钮
    showImportControl: {type: Boolean, default: true}, // 控制是否显示导入控件
    limit: {type: Number}, // 文件上传上限
    maxFileSize: {type: Number, default: 10 * 1024 * 1024}, // 默认10MB
    accept: {type: String}, // 允许上传的文件类型
  },

  computed: {
    fileList() {
      // 直接从 props.value 中解析并返回文件列表
      return JSON.parse(this.value || '[]') || [];
    }
  },

  watch: {
    value() {
      this.toggleUploadButtonVisibility()
    }
  },

  mounted() {
    this.toggleUploadButtonVisibility()
  },

  methods: {
    beforeUpload(file) {
      // 从 accept 属性中提取允许的文件类型
      const allowedTypes = this.accept.split(',').map(type => type.trim().toLowerCase());

      // 获取文件的扩展名
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      // 检查文件扩展名是否在允许的类型中
      if (!allowedTypes.some(type => fileExtension.endsWith(type))) {
        this.$notify.error({
          title: '系统提示',
          message: `文件类型不支持，允许的文件类型: ${allowedTypes.join(', ')}`
        });
        return false; // 阻止文件上传
      }

      // 文件大小限制
      if (file.size > this.maxFileSize) {
        this.$notify.error({
          title: '系统提示',
          message: `文件大小超过限制，最大上传 ${this.maxFileSize / 1024 / 1024} MB`
        });
        return false;
      }

      return true; // 允许上传
    },

    onSuccess(res, file, fileList) {
      if (res.success) {
        // 文件上传数量达到限制时，隐藏上传按钮
        this.toggleUploadButtonVisibility()

        const name = res.result[0].oldName;
        const url = res.result[0].filePath;
        const newFileList = [...this.fileList, {name, url}];
        this.$emit('change', JSON.stringify(newFileList));
        this.$notify.success({title: '系统提示', message: name + ' 上传成功'});
      }
    },

    // 下载文件
    downloadFile(file) {
      const link = document.createElement('a');
      link.href = '/resource/' + file.url;
      link.download = file.name;
      link.click();
    },

    // 删除文件
    removeFile(file) {
      const newFileList = this.fileList.filter(item => item.url !== file.url);
      this.$emit('change', newFileList.length === 0 ? null : JSON.stringify(newFileList));
      this.$notify.success({title: '系统提示', message: file.name + ' 删除成功'});

      // 文件上传数量未达到限制时，显示上传按钮
      this.toggleUploadButtonVisibility()
    },

    // 判断文件是否为图片
    isImage(url) {
      if (!url) return false;
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp'];
      return imageExtensions.some(ext => url.toLowerCase().endsWith(ext));
    },

    onExceed() {
      this.$notify.warning({title: '系统提示', message: '文件数量超出限制'});
    },

    handlePictureCardPreview(file) {
      this.dialogImageUrl = '/resource/' + file.url;
      this.dialogVisible = true;
    },

    // 上传按钮显示、隐藏控制逻辑
    toggleUploadButtonVisibility() {
      this.$nextTick(() => {
        if (this.showImportControl) {
          if (this.fileList.length >= this.limit) {
            this.hideUploadBtn()
          } else {
            this.showUploadBtn()
          }
        } else {
          this.hideUploadBtn()
        }
      })
    },

    // 隐藏上传按钮
    hideUploadBtn() {
      if (this.$el.querySelector) {
        this.$el.querySelector(".el-upload").setAttribute("style", "display:none;");
      }
    },

    // 显示上传按钮
    showUploadBtn() {
      if (this.$el.querySelector) {
        this.$el.querySelector(".el-upload").removeAttribute("style");
      }
    }
  }
}
</script>

<style scoped
       lang="less">
.file-icon, .image-slot, .image-container {
  background-color: #f5f7fa;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 148px !important;
  width: 148px !important;
}

.file-icon i, .image-slot i {
  font-size: 30px;
  color: #909399;
}

.image-container {
  height: 148px !important;
  width: 148px !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* 取消 el-upload 的动画效果 */
/deep/ .el-upload__tip,
/deep/ .el-upload-list__item {
  transition: none !important;
  animation: none !important;
}
</style>
